import { Button, H2, InlineButton, LayoutBand, IconButton, colors, useStatusOverlay, Separator, Row } from '@fs/zion-ui'
import React, { useCallback } from 'react'
import { baseUrl } from '@fs/zion-config'
import { copyToClipboard } from '@fs/zion-frontend-friends'
import { ArrowCaret, ContentCopy } from '@fs/zion-icon'
import { useSlsStateContext } from '@fs/zion-sls'
import { css } from '@emotion/core'
import { useGroupData } from '../imageBrowse/hooks'
import useReturnToAudit from '../../hooks/useReturnToAudit'
import { useOverlayContext } from '../../../shared/providers/overlayContextProvider/OverlayContext'
import transl from './MetadataAuditInfoSheet.transl'
import formatGroupName from '../../utils/formatGroupName'

const smallerTextCss = css`
  font-size: 0.8em;
`

export default function MetadataAuditHeader({ hideEditButton = false }) {
  const showStatusOverlay = useStatusOverlay()
  const { returnToAudit } = useReturnToAudit()
  const zionSlsState = useSlsStateContext()
  const { group } = useGroupData()
  const projectId = group?.internalData?.phoenixAcquisitionIds
  const { openOverlay } = useOverlayContext()

  const imageGroupNumberUrl = `${baseUrl}/ark:/61903/${zionSlsState?.currentImageArk}?view=explore&groupId=${group?.id}&grid=on`
  const auditStandardsUrl =
    'https://www.familysearch.org/en/fieldops/article/natural-group-metadata-standards-and-guidelines'

  const handleCopyClick = useCallback(() => {
    copyToClipboard(group?.naturalGroupId, 'text/plain', () => {
      showStatusOverlay(
        {
          type: 'success',
          message: transl.copySuccess(),
          dismissable: true,
        },
        () => {
          showStatusOverlay({
            type: 'error',
            message: transl.copyError(),
            dismissable: true,
          })
        }
      )
    })
  }, [group, showStatusOverlay])

  return (
    <LayoutBand color={colors.gray02}>
      <Button
        data-testid="back-to-audit-list-button"
        Icon={ArrowCaret}
        iconDirection="backward"
        onClick={() => returnToAudit(projectId)}
      >
        {transl.backToAudit()}
      </Button>
      <Separator size="xs" />
      <H2 size="H5" data-heading>
        {transl.metadataAudit()}
      </H2>
      <Button to={auditStandardsUrl} external target="_blank">
        {transl.qualityStandardsButton()}
      </Button>
      <Row guttersY="nano">
        <div growX>
          <span css={smallerTextCss}>{transl.imageGroupNumber()}</span>
          <br aria-hidden />
          <span>
            <InlineButton
              data-testid="search-image-group-number-button"
              to={imageGroupNumberUrl}
              target="_blank"
              description={transl.goToImageGroup()}
            >
              {formatGroupName(group?.naturalGroupId)}
            </InlineButton>
          </span>
        </div>
        <IconButton
          Icon={ContentCopy}
          data-testid="copy-image-group-number-button"
          title={transl.copyImageGroupNumber()}
          onClick={handleCopyClick}
        />
      </Row>
      {!hideEditButton && (
        <Row guttersY="nano">
          <Button emphasis="medium" onClick={() => openOverlay('editMetadataOverlay')}>
            {transl.edit()}
          </Button>
        </Row>
      )}
      <Separator size="nano" />
    </LayoutBand>
  )
}
